type LogLevel = "debug" | "info" | "warn" | "error";

type LogEntry = {
  timestamp: string;
  level: LogLevel;
  event: string;
  details?: Record<string, unknown>;
};

const LOG_BUFFER_KEY = "markdown-word-addin.logs";
const MAX_LOG_ENTRIES = 200;
let diagnosticsInstalled = false;

function readLogBuffer(): LogEntry[] {
  try {
    const stored = sessionStorage.getItem(LOG_BUFFER_KEY);
    if (!stored) {
      return [];
    }
    const parsed = JSON.parse(stored) as unknown;
    return Array.isArray(parsed) ? (parsed as LogEntry[]) : [];
  } catch {
    return [];
  }
}

function writeLogBuffer(entries: LogEntry[]): void {
  try {
    sessionStorage.setItem(LOG_BUFFER_KEY, JSON.stringify(entries.slice(-MAX_LOG_ENTRIES)));
  } catch {
    // Ignore storage errors in constrained hosts.
  }
}

function stringify(payload: unknown): string {
  try {
    return JSON.stringify(payload);
  } catch {
    return JSON.stringify({
      timestamp: new Date().toISOString(),
      level: "error",
      event: "logger.serialize.failure",
    });
  }
}

function writeConsole(level: LogLevel, payload: LogEntry): void {
  const line = stringify(payload);
  if (level === "error") {
    console.error(line);
    return;
  }
  if (level === "warn") {
    console.warn(line);
    return;
  }
  if (level === "debug") {
    console.debug(line);
    return;
  }
  console.info(line);
}

function safeDetails(details?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!details) {
    return undefined;
  }
  const output: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(details)) {
    output[key] = value instanceof Error ? { message: value.message, stack: value.stack } : value;
  }
  return output;
}

export function log(level: LogLevel, event: string, details?: Record<string, unknown>): void {
  const payload: LogEntry = {
    timestamp: new Date().toISOString(),
    level,
    event,
    details: safeDetails(details),
  };

  const entries = readLogBuffer();
  entries.push(payload);
  writeLogBuffer(entries);
  writeConsole(level, payload);
}

export function installGlobalDiagnostics(): void {
  if (diagnosticsInstalled) {
    return;
  }
  diagnosticsInstalled = true;

  window.addEventListener("error", (errorEvent) => {
    log("error", "window.error", {
      message: errorEvent.message,
      filename: errorEvent.filename,
      line: errorEvent.lineno,
      column: errorEvent.colno,
      error: errorEvent.error instanceof Error ? errorEvent.error : undefined,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    log("error", "window.unhandledrejection", {
      reason: event.reason,
    });
  });
}

export function getRecentLogs(): LogEntry[] {
  return readLogBuffer();
}

export function downloadRecentLogs(): void {
  const logs = getRecentLogs();
  const filename = `markdown-word-diagnostics-${new Date().toISOString().replace(/[:.]/g, "-")}.json`;
  const blob = new Blob([JSON.stringify(logs, null, 2)], { type: "application/json;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
